import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatRupees, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileSignature, ExternalLink, CheckCircle2, XCircle, Download, RefreshCw } from "lucide-react";

const STATUS_PILL = {
  draft:  "bg-slate-100 text-slate-700 border-slate-200",
  sent:   "bg-amber-50 text-amber-800 border-amber-200",
  paid:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  void:   "bg-slate-100 text-slate-600 border-slate-300",
};

export default function PaymentAdvices() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const [items, setItems] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ policy_id: "", due_date: "", amount: "", notes: "" });
  const [paidDlg, setPaidDlg] = useState(null);
  const [paidForm, setPaidForm] = useState({ paid_on: todayISO(), paid_reference: "", roll_next_premium: true });

  const [voidTarget, setVoidTarget] = useState(null);

  const load = async () => {
    const [a, p] = await Promise.all([
      api.get(`/advices${statusFilter === "all" ? "" : `?status=${statusFilter}`}`),
      api.get("/policies"),
    ]);
    setItems(a.data);
    setPolicies(p.data.filter((x) => x.status !== "renewed" && x.status !== "lapsed"));
  };
  useEffect(() => { load(); }, [statusFilter]);

  const create = async () => {
    if (!form.policy_id || !form.due_date || !form.amount) return toast.error("Policy, due date and amount are required");
    try {
      await api.post(`/policies/${form.policy_id}/advices`, {
        due_date: form.due_date, amount: Number(form.amount), notes: form.notes,
      });
      toast.success("Payment advice created");
      setDlg(false); setForm({ policy_id: "", due_date: "", amount: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const markPaid = async () => {
    try {
      await api.post(`/advices/${paidDlg.id}/mark-paid`, paidForm);
      toast.success("Advice marked as paid, next premium date auto-rolled");
      setPaidDlg(null); load();
      window.dispatchEvent(new Event("reminders-changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmVoid = async () => {
    try {
      await api.post(`/advices/${voidTarget.id}/void`);
      setVoidTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async (a) => {
    try {
      const r = await api.get(`/advices/${a.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("Failed to download PDF"); }
  };

  const openPaid = (a) => {
    setPaidDlg(a);
    setPaidForm({ paid_on: todayISO(), paid_reference: "", roll_next_premium: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Compliance</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="advices-title">
            Payment <em className="italic">advices.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Generate a printable advice for any policy premium — with a &ldquo;Pay now&rdquo; link. Marking it paid rolls the next premium date and adds a premium entry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 border border-slate-200 bg-white rounded-md p-1">
            {[["all","All"],["sent","Sent"],["paid","Paid"],["void","Void"]].map(([v,l]) => (
              <button key={v} onClick={() => setStatusFilter(v)} data-testid={`fstat-${v}`}
                className={`px-3 py-1.5 rounded text-[12px] font-medium transition-colors ${
                  statusFilter === v ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}>{l}</button>
            ))}
          </div>
          {canEdit && (
            <Button onClick={() => setDlg(true)} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="new-advice-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New advice
            </Button>
          )}
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Advice #</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Date</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Policy</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Payee</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Amount</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Due</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Status</th>
                <th className="p-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                  <FileSignature className="h-6 w-6 mx-auto text-slate-300 mb-2" />
                  No advices yet.
                </td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`advice-row-${a.id}`}>
                  <td className="p-3 font-mono-fin text-slate-800">{a.advice_number}</td>
                  <td className="p-3">{formatDate(a.date)}</td>
                  <td className="p-3 text-slate-700">{a.policy_title}</td>
                  <td className="p-3 text-slate-600">{a.payee_name || "-"}</td>
                  <td className="p-3 text-right font-mono-fin">{formatRupees(a.amount)}</td>
                  <td className="p-3 text-slate-600">{formatDate(a.due_date)}</td>
                  <td className="p-3">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${STATUS_PILL[a.status]}`} data-testid={`status-${a.id}`}>
                      {a.status}
                    </span>
                    {a.status === "paid" && a.paid_on && (
                      <div className="text-[10px] text-slate-500 mt-0.5">on {formatDate(a.paid_on)}</div>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => downloadPdf(a)} className="p-1 text-slate-500 hover:text-slate-900" title="Download PDF" data-testid={`pdf-${a.id}`}>
                      <Download className="h-4 w-4" />
                    </button>
                    {a.payment_link && (
                      <a href={a.payment_link} target="_blank" rel="noreferrer" className="p-1 text-slate-500 hover:text-red-600 inline-block" title="Pay link">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    {canEdit && a.status !== "paid" && a.status !== "void" && (
                      <>
                        <button onClick={() => openPaid(a)} className="p-1 text-slate-500 hover:text-emerald-600" title="Mark paid" data-testid={`markpaid-${a.id}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => setVoidTarget(a)} className="p-1 text-slate-400 hover:text-red-600" title="Void" data-testid={`void-${a.id}`}>
                          <XCircle className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {items.length === 0 && (
            <li className="p-8 text-center text-slate-500 text-[13px]">
              <FileSignature className="h-6 w-6 mx-auto text-slate-300 mb-2" />
              No advices yet.
            </li>
          )}
          {items.map((a) => (
            <li key={a.id} className="p-3.5" data-testid={`advice-mrow-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-fin text-[12px] text-slate-800">{a.advice_number}</span>
                    <span className={`inline-block text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${STATUS_PILL[a.status]}`}>
                      {a.status}
                    </span>
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-slate-900 truncate">{a.policy_title}</div>
                  <div className="text-[11px] text-slate-500 truncate">{a.payee_name || "—"} · Due {formatDate(a.due_date)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-fin text-[15px] font-semibold text-slate-900 tabular-nums">{formatRupees(a.amount)}</div>
                  <div className="text-[10.5px] text-slate-400">{formatDate(a.date)}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <button onClick={() => downloadPdf(a)}
                  className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded-md px-2 py-1 text-slate-700 hover:bg-slate-50 active:bg-slate-100">
                  <Download className="h-3.5 w-3.5" /> PDF
                </button>
                {a.payment_link && (
                  <a href={a.payment_link} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] border border-red-200 rounded-md px-2 py-1 text-red-700 bg-red-50 active:bg-red-100">
                    <ExternalLink className="h-3.5 w-3.5" /> Pay
                  </a>
                )}
                {canEdit && a.status !== "paid" && a.status !== "void" && (
                  <>
                    <button onClick={() => openPaid(a)}
                      className="inline-flex items-center gap-1 text-[11px] border border-emerald-200 rounded-md px-2 py-1 text-emerald-700 bg-emerald-50 active:bg-emerald-100"
                      data-testid={`markpaid-mobile-${a.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
                    </button>
                    <button onClick={() => setVoidTarget(a)}
                      className="inline-flex items-center gap-1 text-[11px] border border-slate-200 rounded-md px-2 py-1 text-slate-500 active:bg-slate-100 ml-auto">
                      <XCircle className="h-3.5 w-3.5" /> Void
                    </button>
                  </>
                )}
                {a.status === "paid" && a.paid_on && (
                  <span className="text-[10px] text-slate-500 ml-auto">Paid on {formatDate(a.paid_on)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* New advice dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Generate payment advice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Policy</Label>
              <Select value={form.policy_id} onValueChange={(v) => {
                const p = policies.find((x) => x.id === v);
                setForm({ ...form, policy_id: v,
                  amount: p?.amount_paid || "",
                  due_date: p?.next_premium_date || p?.expiry_date || "" });
              }}>
                <SelectTrigger className="mt-1.5" data-testid="adv-policy"><SelectValue placeholder="Pick a policy" /></SelectTrigger>
                <SelectContent>
                  {policies.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title} <span className="text-slate-400">· expires {p.expiry_date}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Amount (Rs)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 font-mono-fin text-right" data-testid="adv-amount" /></div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" data-testid="adv-due" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Optional message on the advice" /></div>
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 rounded">
              The advice will inherit the &quot;Pay now&quot; link from the policy&apos;s payment_link field.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
            <Button onClick={create} className="bg-red-600 hover:bg-red-700 text-white" data-testid="adv-save">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={!!paidDlg} onOpenChange={(v) => !v && setPaidDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark advice {paidDlg?.advice_number} as paid</DialogTitle>
            <DialogDescription>Record the payment date and reference. The next premium date will auto-roll forward.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Paid on</Label><Input type="date" value={paidForm.paid_on} onChange={(e) => setPaidForm({ ...paidForm, paid_on: e.target.value })} className="mt-1.5" data-testid="mp-date" /></div>
            <div><Label>Payment reference (UTR / cheque)</Label><Input value={paidForm.paid_reference} onChange={(e) => setPaidForm({ ...paidForm, paid_reference: e.target.value })} className="mt-1.5 font-mono-fin" data-testid="mp-ref" /></div>
            <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-2">
              <input type="checkbox" checked={paidForm.roll_next_premium} onChange={(e) => setPaidForm({ ...paidForm, roll_next_premium: e.target.checked })} data-testid="mp-roll" />
              <RefreshCw className="h-3.5 w-3.5" />
              Auto-roll next premium date forward by policy frequency
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDlg(null)}>Cancel</Button>
            <Button onClick={markPaid} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mp-save"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Void confirmation */}
      <Dialog open={!!voidTarget} onOpenChange={(v) => !v && setVoidTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Void advice {voidTarget?.advice_number}?</DialogTitle>
            <DialogDescription>This will mark the advice as void. Already-paid advices cannot be voided.</DialogDescription>
          </DialogHeader>
          <p className="sr-only">
            Confirm voiding this advice.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)}>Cancel</Button>
            <Button onClick={confirmVoid} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-void"><XCircle className="h-4 w-4 mr-1.5" /> Void it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
